import Avatar from '@/components/Avatar';
import Badge from '@/components/Badge';
import Stack from '@/components/Stack';
import { Body, Small } from '@/components/Typography';
import styled from '@/styles';
import { WorkerListItem } from '@/services/workersV2API';

import {
  getDisplayedOnboardingPercentage,
  getWorkerBackgroundLabel,
} from '../helpers';
import {
  getI9DisplayLabel,
  getDependabilityBadgeStatus,
  getDependabilityLabel,
} from './StatusMappings';

type Props = {
  worker: WorkerListItem;
  onClick: () => void;
};

const CardWrapper = styled('div', {
  border: '1px solid $neutralA12',
  borderRadius: '$xxl',
  padding: '12px',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: '$neutralA4',
  },
});

const ProgressBarOuter = styled('div', {
  width: '100%',
  height: 6,
  backgroundColor: '$neutralA12',
  borderRadius: '$round',
  overflow: 'hidden',
});

const ProgressBarInner = styled('div', {
  height: '100%',
  borderRadius: '$round',
  transition: 'width 0.3s ease',
  variants: {
    color: {
      green: { backgroundColor: '#45A735' },
      yellow: { backgroundColor: '#F5A623' },
      gray: { backgroundColor: '#999999' },
    },
  },
  defaultVariants: {
    color: 'gray',
  },
});

const StatsRow = styled('div', {
  display: 'flex',
  gap: '16px',
});

const getProgressColor = (pct: number): 'green' | 'yellow' | 'gray' => {
  if (pct >= 100) return 'green';
  if (pct >= 50) return 'yellow';
  return 'gray';
};

const i9BadgeStatus: Record<string, 'theme' | 'warning' | 'neutral'> = {
  Completed: 'theme',
  Pending: 'warning',
  'In Progress': 'warning',
  'Pending E-Verify': 'warning',
};

const bgBadgeStatus: Record<string, 'theme' | 'danger' | 'warning'> = {
  Approved: 'theme',
  Declined: 'danger',
  Pending: 'warning',
};

const WorkerCard = ({ worker, onClick }: Props) => {
  const onboardingPercentage = getDisplayedOnboardingPercentage(worker);
  const i9Label = getI9DisplayLabel(worker.statuses.i9_status);
  const bgLabel = getWorkerBackgroundLabel(worker);

  return (
    <CardWrapper onClick={onClick}>
      <Stack vertical gap={10}>
        {/* Row 1: Avatar + Name + Phone */}
        <Stack gap={10}>
          <Avatar
            size="sm"
            rounded
            src={worker.avatar_url}
            firstName={worker.user.first_name}
            lastName={worker.user.last_name}
            initialsFormat="firstLast"
          />
          <Stack vertical gap={2}>
            <Body weight="semibold">{worker.user.full_name}</Body>
            {worker.user.phone_number && (
              <Small color="lighter">{worker.user.phone_number}</Small>
            )}
          </Stack>
        </Stack>

        {/* Row 2: Onboarding progress bar */}
        <Stack vertical gap={4}>
          <Small color="lighter">Onboarding: {onboardingPercentage}%</Small>
          <ProgressBarOuter>
            <ProgressBarInner
              color={getProgressColor(onboardingPercentage)}
              css={{ width: `${onboardingPercentage}%` }}
            />
          </ProgressBarOuter>
        </Stack>

        {/* Row 3: Badge chips */}
        <Stack gap={6} style={{ flexWrap: 'wrap' }}>
          <Badge
            label={`I-9: ${i9Label}`}
            status={i9BadgeStatus[i9Label] || 'neutral'}
          />
          <Badge
            label={`BG: ${bgLabel}`}
            status={bgBadgeStatus[bgLabel] || 'neutral'}
          />
          <Badge
            label={getDependabilityLabel(
              worker.dependability_tier,
              worker.accountability_status,
              worker.is_suspended,
            )}
            status={getDependabilityBadgeStatus(
              worker.dependability_tier,
              worker.accountability_status,
              worker.is_suspended,
            )}
          />
        </Stack>

        {/* Row 4: Stats */}
        <StatsRow>
          <Small color="lighter">Shifts: {worker.jobs_worked}</Small>
          <Small color="lighter">
            Rating:{' '}
            {worker.average_rating != null
              ? worker.average_rating.toFixed(1)
              : 'N/A'}
          </Small>
        </StatsRow>
      </Stack>
    </CardWrapper>
  );
};

export default WorkerCard;
